/**
 * <photo-gallery-slider>
 *
 * Custom element that wraps a Swiper.js instance for a variable-width
 * photo gallery slider. Each slide sets its own aspect-ratio; the section
 * fixes the height so widths differ naturally across slides.
 *
 * Expects:
 *   .pgs__swiper   – the Swiper root element
 *   .pgs__progress-fill – fill bar indicating scroll progress
 *   .pgs__btn--prev / .pgs__btn--next – navigation buttons
 *
 * Data attributes on the host element:
 *   data-gap  – space between slides in px (default: 12)
 */
class PhotoGallerySlider extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {import('swiper').Swiper | null} */
  #modalSwiper = null;

  /** @type {HTMLElement | null} */
  #modal = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeydown = null;

  connectedCallback() {
    if (typeof Swiper !== 'undefined') {
      this.#init();
    } else {
      // Wait for the Swiper bundle script to finish loading
      const swiperScript = document.querySelector('script[src*="swiper-bundle"]');
      if (swiperScript) {
        swiperScript.addEventListener('load', () => this.#init(), { once: true });
      }
    }
  }

  disconnectedCallback() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#modalSwiper?.destroy(true, true);
    this.#modalSwiper = null;
    if (this.#onKeydown) {
      document.removeEventListener('keydown', this.#onKeydown);
      this.#onKeydown = null;
    }
    document.body.classList.remove('pgs-modal-open');
  }

  #init() {
    const swiperEl = this.querySelector('.pgs__swiper');
    if (!swiperEl) return;

    const progressFill = this.querySelector('.pgs__progress-fill');
    const prevBtn = this.querySelector('.pgs__btn--prev');
    const nextBtn = this.querySelector('.pgs__btn--next');
    const gapDesktop = parseInt(this.dataset.gap ?? '43', 10);
    const gapMobile = parseInt(this.dataset.gapMobile ?? '12', 10);

    this.#swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: gapDesktop,
      grabCursor: true,
      watchSlidesProgress: true,
      a11y: false, // we provide our own accessible buttons
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
        disabledClass: 'pgs__btn--disabled',
      },
      breakpoints: {
        // mobile: centered active slide, partial peek of adjacent slides
        0: {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: gapMobile,
        },
        // desktop: variable-width natural sizes
        750: {
          slidesPerView: 'auto',
          centeredSlides: false,
          spaceBetween: gapDesktop,
        },
      },
    });

    // Wire progress bar
    if (progressFill) {
      this.#swiper.on('progress', (swiper) => {
        this.#updateProgress(swiper, progressFill);
        this.#updateNav(prevBtn, nextBtn);
      });
      this.#updateProgress(this.#swiper, progressFill);
    }

    // Also update nav on slideChange (covers edge cases)
    if (prevBtn || nextBtn) {
      this.#swiper.on('slideChange', () => this.#updateNav(prevBtn, nextBtn));
      this.#updateNav(prevBtn, nextBtn);
    }

    this.#initModal();

    // Re-init on Shopify theme editor section reload
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this)) {
        this.#swiper?.destroy(true, true);
        this.#modalSwiper?.destroy(true, true);
        this.#modalSwiper = null;
        this.#init();
      }
    }, { once: true });
  }

  /**
   * Wire up the click-to-open modal that swipes through all photos.
   */
  #initModal() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    this.#modal = document.querySelector(`[data-pgs-modal="${sectionId}"]`);
    if (!this.#modal) return;

    // Open the modal when a slide trigger is clicked.
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-pgs-open]');
      if (!trigger || !this.contains(trigger)) return;
      const index = parseInt(trigger.dataset.pgsIndex ?? '0', 10);
      this.#openModal(index);
    });

    // Close handlers (backdrop + close button).
    this.#modal.querySelectorAll('[data-pgs-close]').forEach((el) => {
      el.addEventListener('click', () => this.#closeModal());
    });

    // Delegated prev/next inside the modal.
    this.#modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-pgs-modal-prev]')) {
        this.#modalSwiper?.slidePrev();
      } else if (event.target.closest('[data-pgs-modal-next]')) {
        this.#modalSwiper?.slideNext();
      }
    });

    this.#onKeydown = (event) => {
      if (event.key === 'Escape' && !this.#modal?.hidden) {
        this.#closeModal();
      }
    };
    document.addEventListener('keydown', this.#onKeydown);
  }

  /**
   * Lazily create the modal Swiper instance.
   * @param {number} initialSlide
   */
  #ensureModalSwiper(initialSlide) {
    const swiperEl = this.#modal?.querySelector('[data-pgs-modal-swiper]');
    if (!swiperEl) return;

    if (this.#modalSwiper) {
      this.#modalSwiper.slideTo(initialSlide || 0, 0);
      this.#modalSwiper.update();
      this.#syncModalNav();
      return;
    }

    this.#modalSwiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      grabCursor: false,
      allowTouchMove: false,
      loop: false,
      speed: 280,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      initialSlide: initialSlide || 0,
      a11y: false,
      on: {
        slideChange: () => this.#syncModalNav(),
      },
    });
    this.#syncModalNav();
  }

  /**
   * Toggle disabled state on the modal prev/next buttons across all slides.
   */
  #syncModalNav() {
    if (!this.#modalSwiper || !this.#modal) return;
    const prevBtns = this.#modal.querySelectorAll('[data-pgs-modal-prev]');
    const nextBtns = this.#modal.querySelectorAll('[data-pgs-modal-next]');
    prevBtns.forEach((btn) => btn.toggleAttribute('disabled', this.#modalSwiper.isBeginning));
    nextBtns.forEach((btn) => btn.toggleAttribute('disabled', this.#modalSwiper.isEnd));
  }

  /**
   * Open the modal at the given slide index.
   * @param {number} index
   */
  #openModal(index) {
    if (!this.#modal) return;
    this.#modal.hidden = false;
    this.#modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pgs-modal-open');
    requestAnimationFrame(() => {
      this.#ensureModalSwiper(index || 0);
      this.#modalSwiper?.update();
      // Allow the browser a frame to apply the un-hidden state before animating.
      requestAnimationFrame(() => this.#modal?.classList.add('is-open'));
    });
  }

  /**
   * Close the modal.
   */
  #closeModal() {
    if (!this.#modal) return;
    this.#modal.classList.remove('is-open');
    document.body.classList.remove('pgs-modal-open');
    const modal = this.#modal;
    const onEnd = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    };
    const dialog = modal.querySelector('.pgs-modal__dialog');
    if (dialog) {
      dialog.addEventListener('transitionend', onEnd, { once: true });
      // Fallback in case transitionend doesn't fire.
      setTimeout(onEnd, 400);
    } else {
      onEnd();
    }
  }

  /**
   * Update the progress fill bar width based on scroll progress (0–1).
   * @param {import('swiper').Swiper} swiper
   * @param {HTMLElement} fill
   */
  #updateProgress(swiper, fill) {
    const trackWidth = fill.parentElement?.offsetWidth ?? 181;
    const minWidth = Math.round(trackWidth * 0.16); // ~16 % minimum visible fill
    const fillWidth = Math.round(minWidth + swiper.progress * (trackWidth - minWidth));
    fill.style.width = `${Math.min(fillWidth, trackWidth)}px`;
  }

  /**
   * Toggle disabled state on prev/next buttons.
   * @param {HTMLButtonElement | null} prev
   * @param {HTMLButtonElement | null} next
   */
  #updateNav(prev, next) {
    if (!this.#swiper) return;
    if (prev) {
      prev.disabled = this.#swiper.isBeginning;
      prev.setAttribute('aria-disabled', String(this.#swiper.isBeginning));
    }
    if (next) {
      next.disabled = this.#swiper.isEnd;
      next.setAttribute('aria-disabled', String(this.#swiper.isEnd));
    }
  }
}

if (!customElements.get('photo-gallery-slider')) {
  customElements.define('photo-gallery-slider', PhotoGallerySlider);
}
